/**
 * Add Aggregation Modal
 *
 * Creates one or more 'aggregation' edges from a single parent to multiple
 * children in a single submission. The parent picker is pre-populated from
 * the item-detail context when sourceItemId is provided.
 *
 * Aggregation is bottom-up: children assert upward into the parent.
 * Canon ref: 07-item-relationship-taxonomy-and-mutation-rules.md
 */

import { useState, useEffect } from 'react';
import { X, Box, AlertCircle, XCircle, Plus } from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { SearchableItemPicker } from '../SearchableItemPicker';

interface AddAggregationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (edgeId: string) => void;
  /** Pre-populate the parent picker when opened from an item detail page. */
  sourceItemId?: string;
}

export function AddAggregationModal({
  isOpen,
  onClose,
  onCreated,
  sourceItemId,
}: AddAggregationModalProps) {
  const createEdge = useItemRelationshipsStore((s) => s.createEdge);
  const fetchFromSupabase = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const allItems = useItemsStore((s) => s.items);

  const [parentItemId, setParentItemId] = useState<string>(sourceItemId ?? '');
  const [childItemIds, setChildItemIds] = useState<string[]>([]);
  const [pickerKey, setPickerKey] = useState(0);
  const [rationale, setRationale] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      void fetchItems();
      void fetchFromSupabase();
    }
  }, [isOpen, fetchItems, fetchFromSupabase]);

  useEffect(() => {
    if (!isOpen) return;
    setParentItemId(sourceItemId ?? '');
    setChildItemIds([]);
    setPickerKey((k) => k + 1);
    setRationale('');
    setError(null);
  }, [isOpen, sourceItemId]);

  const handleAddChild = (id: string | undefined) => {
    if (!id) return;
    if (id === parentItemId) { setError('Child cannot be the same as the parent'); return; }
    if (childItemIds.includes(id)) return;
    setError(null);
    setChildItemIds((prev) => [...prev, id]);
    setPickerKey((k) => k + 1);
  };

  const handleRemoveChild = (id: string) => {
    setChildItemIds((prev) => prev.filter((c) => c !== id));
  };

  const getTitle = (id: string) =>
    allItems.find((i) => i.id === id)?.title ?? id;

  const handleCreate = async () => {
    setError(null);
    if (!parentItemId) { setError('Select the parent item'); return; }
    if (childItemIds.length === 0) { setError('Add at least one child item'); return; }

    setIsCreating(true);
    let lastEdgeId: string | null = null;
    try {
      for (const childId of childItemIds) {
        const edge = await createEdge({
          type: 'aggregation',
          directionality: 'directed',
          status: 'active',
          declaredMode: 'declared',
          sourceItemId: parentItemId,
          targetItemId: childId,
          rationale: rationale.trim() || undefined,
        });
        if (edge) lastEdgeId = edge.id;
      }
      if (lastEdgeId) {
        onCreated?.(lastEdgeId);
        onClose();
      } else {
        setError('Failed to create aggregation');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create aggregation');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  const canSubmit = !!parentItemId && childItemIds.length > 0 && !isCreating;
  const excludeIds = [parentItemId, ...childItemIds].filter(Boolean);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <Box className="h-6 w-6 text-[var(--green)]" />
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Add Aggregation</h2>
              <p className="text-sm text-[var(--text-ter)]">Bottom-up composition — children roll up into parent</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
              Parent item <span className="text-[var(--text-ter)] font-normal">(the aggregate / container)</span>
            </label>
            <SearchableItemPicker
              value={parentItemId || undefined}
              onChange={(id) => setParentItemId(id ?? '')}
              excludeId={childItemIds[0] || undefined}
              label=""
              placeholder="Search for the parent item..."
              lifecycleStates={[]}
              allowEmpty={false}
              allowCreate
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
              Child items <span className="text-[var(--text-ter)] font-normal">(constituent parts)</span>
            </label>

            {childItemIds.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {childItemIds.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--green-dim)] border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)] text-sm rounded-full"
                  >
                    {getTitle(id)}
                    <button
                      type="button"
                      onClick={() => handleRemoveChild(id)}
                      className="text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2">
              <div className="flex-1">
                <SearchableItemPicker
                  key={pickerKey}
                  value={undefined}
                  onChange={handleAddChild}
                  excludeIds={excludeIds}
                  label=""
                  placeholder="Search to add a child item…"
                  lifecycleStates={[]}
                  allowEmpty
                  allowCreate
                />
              </div>
              <div className="text-[var(--text-ter)] flex-shrink-0">
                <Plus className="h-4 w-4" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Rationale <span className="text-[var(--text-ter)] font-normal">(optional)</span></label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Why do these items compose into the parent?"
              rows={2}
              className="w-full px-4 py-2 border border-[var(--rule)] resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-[var(--surface)]">
          <button onClick={onClose} className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)]">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!canSubmit}
            className="px-4 py-2 bg-[var(--green)] text-white font-medium hover:bg-[var(--green)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating
              ? 'Adding…'
              : `Add aggregation${childItemIds.length > 1 ? ` (${childItemIds.length} children)` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
